/**
 * Packing List PDF — layout cercano al documento comercial (sin fondos de color).
 */
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ptcImage from '../../imagenes/ptc.jpg';
import summaryStampImage from '../../imagenes/palmtrade-summary-stamp.png';
import { parseExcelNumber } from './parsePackingListExcel';
import { isRwNrPackingListRow } from './packingListRwNr';
import { invoiceNumberCorrelativo } from '../invoice/invoiceNumberUtils';
import { packingSummaryLayoutConstants, yForPackingSummaryBlock } from './packingListSummaryLayout';
import { resolvePackingListSummaryText } from './packingListSummaryText';
import { consolidatePackingDetailForPdf } from './packingListPdfConsolidate';

export { estimatePackingSummaryBlockHeightMm } from './packingListSummaryLayout';
export {
  consolidatePackingDetailForPdf,
  packingPdfLineMergeKey,
} from './packingListPdfConsolidate';

const COMPANY_LINES = [
  'PALM TRADE CHILE SPA',
  'Avenida Presidente Kennedy 5146, oficina 51',
  'Vitacura, Santiago, Chile',
  'Fono: (56-2) 22190119 - Fax: (56-2) 22184390',
];

const RUT_EMPRESA = '77.710.540-K';

const SUMMARY_TABLE_ID = 'packing-list-summary';

/** ~1 línea a 8pt en mm: columna izquierda baja para que el texto central arranque más arriba. */
const SUMMARY_LEFT_EXTRA_TOP_PAD = 3.6;
const { SUMMARY_TITLE_LINE_H, SUMMARY_TITLE_GAP } = packingSummaryLayoutConstants;

/** Elimina hojas finales vacías que jsPDF/autoTable pueden dejar al paginar. */
function removeTrailingBlankPages(doc) {
  while (doc.internal.getNumberOfPages() > 1) {
    const n = doc.internal.getNumberOfPages();
    doc.setPage(n);
    const pageData = doc.internal.pages[n];
    const content = Array.isArray(pageData) ? pageData.join('') : String(pageData || '');
    const hasText = /\)\s*Tj/.test(content) || /\)\s*'\s/.test(content);
    const hasImage = /\/I\d+\s+Do/.test(content);
    const hasFill = /\bre\s+B\s+f\s+re\s+S\b/.test(content) || /\bre\s+W\s+n\s+re\s+f\b/.test(content);
    if (hasText || hasImage || hasFill) break;
    doc.deletePage(n);
  }
}

const GRID = [0, 0, 0];
const LINE = 0.12;

function fmtNumCL(v, decimals = 3) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(v));
}

/** Enteros con separador de miles (es-CL), sin decimales — p. ej. Total pieces. */
function fmtIntegerCL(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('es-CL', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v));
}

/** Width / Length: sin decimales, miles con coma (ej. 1220 → 1,220). */
function fmtMeasureWidthLength(v) {
  if (v == null || v === '' || Number.isNaN(Number(v))) return '';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(v));
}

/** Totales Summary: etiquetas a la izquierda, números alineados a la derecha (sin BUNDLES). */
function summaryTotalsCellLines(doc, innerWidthMm, bundlesValue, sumM3, sumPesoGross) {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const spW = Math.max(0.2, doc.getTextWidth(' ') || 0.35);
  const rows = [
    ['Total Bundles', String(bundlesValue)],
    ['Total cbm or m³', fmtNumCL(sumM3, 3)],
    ['Total Gross Weight (Kg)', fmtNumCL(sumPesoGross, 3)],
  ];
  const labelColW =
    Math.max(...rows.map(([label]) => doc.getTextWidth(`${label}:`))) + spW;
  /** Borde derecho de los números: mitad del espacio libre tras las etiquetas. */
  const valueRightX = labelColW + (innerWidthMm - labelColW) * 0.5;

  return rows
    .map(([label, v]) => {
      const l = `${label}:`;
      const vs = String(v);
      const padAfterLabel = Math.max(0, Math.floor((labelColW - doc.getTextWidth(l)) / spW));
      const padBeforeValue = Math.max(
        0,
        Math.floor((valueRightX - labelColW - doc.getTextWidth(vs)) / spW)
      );
      return `${l}${' '.repeat(padAfterLabel)}${' '.repeat(padBeforeValue)}${vs}`;
    })
    .join('\n');
}

/** HS en línea tipo "4408 90 90" a partir de NCM sin espacios. */
function fmtDateLong(isoDate) {
  if (!isoDate) return '';
  const d = new Date(isoDate + (isoDate.length === 10 ? 'T12:00:00' : ''));
  if (Number.isNaN(d.getTime())) return String(isoDate);
  return d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' });
}

function safe(s) {
  return s == null ? '' : String(s);
}

// Parche exclusivo de Packing List: Brasil -> Brazil.
function normalizeCountryForPackingList(s) {
  const t = safe(s).trim();
  if (!t) return '';
  return /^brasil$/i.test(t) ? 'Brazil' : t;
}

function normalizePortLabelForPackingList(label) {
  const t = safe(label).trim();
  if (!t) return '';
  return t.replace(/\((\s*Brasil\s*)\)/i, '(Brazil)');
}

/** Nombre de puerto sin sufijo " (País)" — p. ej. Loading Port en PDF. */
function portNameWithoutCountry(label) {
  const t = safe(label).trim();
  if (!t) return '';
  return t.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

/** "Puerto (País)" → "Puerto, País" — p. ej. Unloading Port en PDF. */
function portNameCommaCountry(label) {
  const t = normalizePortLabelForPackingList(label);
  if (!t) return '';
  const m = t.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!m) return t;
  const port = m[1].trim();
  const country = normalizeCountryForPackingList(m[2].trim());
  return country ? `${port}, ${country}` : port;
}

/** Valores en mayúsculas como en el documento impreso. */
function toUpperDisplay(s) {
  const t = safe(s).trim();
  return t ? t.toUpperCase() : '';
}

function withPinCodeAboveCountry(addrLines, clienteCodigoPostal) {
  const lines = [...addrLines];
  const hasPin = lines.some((ln) => /^PIN CODE\s*:/i.test(ln));
  const pin = safe(clienteCodigoPostal).trim();
  if (!pin || hasPin) return lines;

  const pinLine = `PIN CODE: ${pin}`.toUpperCase();
  if (lines.length === 0) return [pinLine];
  lines.push(pinLine);
  return lines;
}

/** Quita líneas de dirección que solo repiten el país (ya va en Destination Country / City-Country). */
function stripCountryFromAddressLines(addrLines, pais) {
  const country = toUpperDisplay(normalizeCountryForPackingList(pais));
  if (!country) return addrLines;
  return addrLines.filter((ln) => toUpperDisplay(normalizeCountryForPackingList(ln)) !== country);
}

function netVal(r) {
  const n = r.peso_neto != null && r.peso_neto !== '' ? Number(r.peso_neto) : null;
  if (n != null && !Number.isNaN(n)) return n;
  const gross = r.peso != null && r.peso !== '' ? Number(r.peso) : null;
  return gross != null && !Number.isNaN(gross) ? gross : null;
}

function grossStr(r) {
  return r.peso != null ? fmtNumCL(r.peso, 3) : '';
}

function netStr(r) {
  const n = r.peso_neto != null && r.peso_neto !== '' ? Number(r.peso_neto) : null;
  if (n != null && !Number.isNaN(n)) return fmtNumCL(n, 3);
  return r.peso != null ? fmtNumCL(r.peso, 3) : '';
}

function cellEspesor(r) {
  const e = r.espesor;
  if (e == null || e === '') return '';
  if (typeof e === 'number' && !Number.isNaN(e)) return `${fmtNumCL(e, 1)} MM`;
  const n = parseFloat(String(e).replace(',', '.'));
  return Number.isFinite(n) ? `${fmtNumCL(n, 1)} MM` : safe(e);
}

function cellNum3CL(v) {
  if (v == null || v === '') return '';
  return fmtNumCL(v, 3);
}

function cbmPerBundleStr(r) {
  const totalM3 = Number(r.total_m3);
  const bundles = Number(r.cantidad_paquetes);
  if (!Number.isFinite(totalM3) || !Number.isFinite(bundles) || bundles === 0) return '';
  return fmtNumCL(totalM3 / bundles, 3);
}

/** Total M³ del packing list: cabecera guardada o suma de líneas. */
function computeSumM3(detalleExcel, totalVolumenM3) {
  if (totalVolumenM3 != null && totalVolumenM3 !== '' && Number.isFinite(Number(totalVolumenM3))) {
    return Number(totalVolumenM3);
  }
  const rows = detalleExcel || [];
  const tieneM3 = rows.some(
    (r) => r.total_m3 != null && r.total_m3 !== '' && Number.isFinite(Number(r.total_m3))
  );
  if (!tieneM3) return null;
  return rows.reduce((s, r) => s + (Number(r.total_m3) || 0), 0);
}

function productsCell(r) {
  const t = safe(r.productos_texto);
  if (t) return t;
  return (
    [safe(r.grado_celda), safe(r.proveedor_celda), safe(r.almacen_celda)].filter(Boolean).join(' / ')
  );
}

function piecesPerBundleCell(r) {
  return isRwNrPackingListRow(r) ? '' : safe(r.piezas_por_paquete);
}

function measureWidthLengthCell(val) {
  const n = parseExcelNumber(val);
  return n != null ? fmtMeasureWidthLength(n) : '';
}

function widthCell(r) {
  const w = measureWidthLengthCell(r.ancho);
  if (w) return w;
  const txt = [safe(r.productos_texto), safe(r.grado_celda)].join(' ').toUpperCase();
  return /\bRW\b/.test(txt) ? 'RW' : safe(r.ancho);
}

function containerPdf(r) {
  const c = safe(r.container_celda);
  if (c) return c;
  return safe(r._container_excel) || (r._container != null ? String(r._container) : '');
}

function detailContainerKey(r) {
  return containerPdf(r).trim();
}

/** Fila vacía entre bloques de contenedor (bordes visibles, sin relleno). */
function detailContainerSeparatorRow() {
  return Array.from({ length: 13 }, () => ({
    content: '',
    styles: {
      minCellHeight: 2.2,
      fillColor: [255, 255, 255],
      lineColor: GRID,
      lineWidth: LINE,
    },
  }));
}

function sameMergeGroup(a, b) {
  const ca = safe(a.container_celda).trim() || safe(a._container_excel).trim();
  const cb = safe(b.container_celda).trim() || safe(b._container_excel).trim();
  if (!ca || ca !== cb) return false;
  return grossStr(a) === grossStr(b) && netStr(a) === netStr(b);
}

/**
 * Suma bruto/neto contando una vez por grupo que el PDF fusiona (mismas celdas Gross/Net
 * y contenedor). Si el peso es total de contenedor repetido en cada subfila, un reduce
 * simple lo multiplicaba.
 */
function sumPesoGrossByMergeGroups(detalleExcel) {
  let s = 0;
  let hasValue = false;
  let i = 0;
  while (i < detalleExcel.length) {
    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    const peso = detalleExcel[i].peso;
    if (peso != null && peso !== '' && Number.isFinite(Number(peso))) {
      s += Number(peso);
      hasValue = true;
    }
    i = j;
  }
  return hasValue ? s : null;
}

function sumPesoNetByMergeGroups(detalleExcel) {
  let s = 0;
  let hasValue = false;
  let i = 0;
  while (i < detalleExcel.length) {
    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    const peso = netVal(detalleExcel[i]);
    if (peso != null) {
      s += peso;
      hasValue = true;
    }
    i = j;
  }
  return hasValue ? s : null;
}

/**
 * @param {object} params
 * @param {string} [params.productDescription]
 * @param {string} [params.hsCode]
 * @param {string} [params.summaryAdditionalText]
 */
export function buildPackingListPdfDoc(params) {
  const {
    numeroInvoice,
    fechaEmbarque,
    clienteNombre,
    clienteDireccion,
    clienteCodigoPostal,
    poLabel,
    paisOrigen,
    paisDestino,
    nombreNave,
    nombrePuertoOrigen,
    nombrePuertoDestino,
    lugarEntrega,
    nombreBl,
    totalPaquetes,
    totalContenedores,
    detalleExcel = [],
    totalVolumenM3,
    productDescription = '',
    hsCode = '',
    summaryAdditionalText = '',
  } = params;

  /** A4 horizontal: más ancho para la tabla de detalle al imprimir. */
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentW = pageW - 2 * margin;
  const rightX = pageW - margin;

  const logoW = 28;
  const imgProps = doc.getImageProperties(ptcImage);
  const logoH = (imgProps.height * logoW) / imgProps.width;
  let y = 10;

  doc.addImage(ptcImage, 'JPEG', margin, y, logoW, logoH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.text('Packing List', rightX, y + 14, { align: 'right' });
  doc.setFontSize(13);
  const inv = invoiceNumberCorrelativo(numeroInvoice) || '';
  doc.text(inv, rightX, y + 21, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(`Date: ${fmtDateLong(fechaEmbarque)}`, rightX, y + 27, { align: 'right' });

  y = y + logoH + 5;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  COMPANY_LINES.forEach((line) => {
    doc.text(line, margin, y);
    y += 3.4;
  });
  y += 4;

  const addrLinesRaw = safe(clienteDireccion)
    .split(/\r?\n/)
    .map((ln) => ln.trim())
    .filter(Boolean)
    .map((ln) => ln.toUpperCase());
  const addrLines = withPinCodeAboveCountry(
    stripCountryFromAddressLines(addrLinesRaw, paisDestino),
    clienteCodigoPostal
  );
  const customerBlock = ['Customer:', toUpperDisplay(clienteNombre), ...addrLines].join('\n');

  /** Bloque cliente + envío: una sola grilla (sin hueco), 75% del ancho de página, alineada al margen izquierdo. */
  const blockW = pageW * 0.75;
  const blockRightMargin = pageW - margin - blockW;
  const shipColRatios = [16, 16, 16, 26, 66, 28, 30, 30, 32];
  const shipColRatioSum = shipColRatios.reduce((a, b) => a + b, 0);
  const shipColumnStyles = {};
  for (let i = 0; i < shipColRatios.length; i++) {
    shipColumnStyles[i] = { cellWidth: (blockW * shipColRatios[i]) / shipColRatioSum };
  }

  const sumM3 = computeSumM3(detalleExcel, totalVolumenM3);
  const sumM3HeaderTxt =
    sumM3 != null && sumM3 !== '' && Number.isFinite(Number(sumM3)) ? fmtNumCL(sumM3, 3) : '';

  const shipHeaders = [
    'Total Packages',
    'Total cbm or m³',
    'Container',
    'Destination Country',
    'Vessel Name',
    'Loading Port',
    'Unloading Port',
    'Place of delivery',
    'BL',
  ];
  const shipRow = [
    totalPaquetes != null && totalPaquetes !== '' ? safe(totalPaquetes) : '',
    sumM3HeaderTxt,
    totalContenedores !== '' && totalContenedores != null ? safe(totalContenedores) : '',
    toUpperDisplay(normalizeCountryForPackingList(paisDestino)),
    toUpperDisplay(nombreNave),
    toUpperDisplay(portNameWithoutCountry(nombrePuertoOrigen)),
    toUpperDisplay(portNameCommaCountry(nombrePuertoDestino)),
    toUpperDisplay(lugarEntrega),
    toUpperDisplay(nombreBl),
  ];

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: blockRightMargin },
    tableWidth: blockW,
    body: [
      [
        {
          content: customerBlock,
          rowSpan: 2,
          colSpan: 5,
          styles: { fontSize: 8, valign: 'top', halign: 'left' },
        },
        { content: 'Contract Number', styles: { fontStyle: 'bold', halign: 'left', valign: 'middle', fontSize: 8 } },
        { content: toUpperDisplay(poLabel), colSpan: 3, styles: { halign: 'center', valign: 'middle', fontSize: 8 } },
      ],
      [
        { content: 'Country of Origin', styles: { fontStyle: 'bold', halign: 'left', valign: 'middle', fontSize: 8 } },
        { content: toUpperDisplay(normalizeCountryForPackingList(paisOrigen)), colSpan: 3, styles: { halign: 'center', valign: 'middle', fontSize: 8 } },
      ],
      shipHeaders.map((h) => ({
        content: h,
        styles: {
          fontStyle: 'bold',
          fontSize: 7,
          halign: 'center',
          valign: 'middle',
          fillColor: [255, 255, 255],
          textColor: [0, 0, 0],
        },
      })),
      shipRow.map((cell) => ({
        content: cell,
        styles: { fontSize: 7, halign: 'center', valign: 'middle', fontStyle: 'normal' },
      })),
    ],
    columnStyles: shipColumnStyles,
    styles: {
      lineColor: GRID,
      lineWidth: LINE,
      textColor: [0, 0, 0],
      cellPadding: 1.1,
      valign: 'middle',
    },
    theme: 'grid',
  });

  y = doc.lastAutoTable.finalY + 5;

  /** PDF: consolidar por contenedor + grado/medida/pcs (no separar por NF). */
  const detallePdf = consolidatePackingDetailForPdf(detalleExcel);

  const sumPesoGross = sumPesoGrossByMergeGroups(detallePdf);
  const sumPesoNet = sumPesoNetByMergeGroups(detallePdf);
  const bundlesTxt =
    totalPaquetes != null && totalPaquetes !== '' ? `${totalPaquetes} BUNDLES` : '';
  const bundlesSummaryTxt =
    totalPaquetes != null && totalPaquetes !== '' ? fmtIntegerCL(totalPaquetes) : '';
  const totalBundlesNumberOnly =
    totalPaquetes != null && totalPaquetes !== '' ? String(totalPaquetes) : '';

  const headRow1 = [
    { content: 'Contract', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Products', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Bundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Measures', colSpan: 3, styles: { halign: 'center', valign: 'middle' } },
    { content: 'Pieces per\nBundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Total\npieces', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'cbm or m3\nper Bundles', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Total Vol.\ncbm or m3', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Gross\nWeight kgs', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Net\nWeight kgs', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
    { content: 'Container', rowSpan: 2, styles: { valign: 'middle', halign: 'center' } },
  ];

  const headRow2 = [{ content: 'Thickness' }, { content: 'Width' }, { content: 'Length' }];

  const bodyRows = buildDetailBody(detallePdf);

  /** Pie: solo número en Bundles; "X BUNDLES" junto en Measures (3 cols); vol. total y pesos. */
  const footRow = [
    '',
    '',
    totalBundlesNumberOnly,
    {
      content: bundlesTxt,
      colSpan: 3,
      styles: { halign: 'center', valign: 'middle', fontStyle: 'bold' },
    },
    '',
    '',
    '',
    fmtNumCL(sumM3, 3),
    fmtNumCL(sumPesoGross, 3),
    fmtNumCL(sumPesoNet, 3),
    '',
  ];

  if (bodyRows.length === 0) {
    bodyRows.push([
      '',
      'Sin líneas cargadas desde Excel',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
      '',
    ]);
  }

  /** Tabla de detalle: 13 columnas (misma anchura útil que Summary). Ratios proporcionales, suma = contentW. */
  const detailColRatios = [18, 30, 12, 11, 11, 11, 13, 12, 13, 13, 13, 13, 24];
  const detailColRatioSum = detailColRatios.reduce((a, b) => a + b, 0);
  const detailColumnStyles = {};
  for (let i = 0; i < detailColRatios.length; i++) {
    detailColumnStyles[i] = { cellWidth: (contentW * detailColRatios[i]) / detailColRatioSum };
  }

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [headRow1, headRow2],
    body: bodyRows,
    foot: [footRow],
    showFoot: 'lastPage',
    styles: {
      fontSize: 6.5,
      cellPadding: 0.9,
      valign: 'middle',
      halign: 'center',
      lineColor: GRID,
      lineWidth: LINE,
      textColor: [0, 0, 0],
    },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
      fontSize: 6.5,
    },
    footStyles: {
      fillColor: [255, 255, 255],
      textColor: [0, 0, 0],
      fontSize: 6.5,
      fontStyle: 'bold',
      lineColor: GRID,
    },
    columnStyles: detailColumnStyles,
    theme: 'grid',
    didDrawPage: (data) => {
      if (data.pageNumber > 1) {
        const d = data.doc;
        d.setFontSize(7);
        d.setTextColor(80, 80, 80);
        d.text(`Packing List ${inv}`, margin, 8);
        d.setTextColor(0, 0, 0);
      }
    },
  });

  const pageH = doc.internal.pageSize.getHeight();
  y = yForPackingSummaryBlock(doc, doc.lastAutoTable.finalY, margin, pageH).y;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  const summaryTitleBaseline = y + SUMMARY_TITLE_LINE_H;
  doc.text('Summary', margin, summaryTitleBaseline);
  y = summaryTitleBaseline + SUMMARY_TITLE_GAP;

  const summaryCol0InnerW = contentW * 0.34 - 4;
  const totalsCellContent = summaryTotalsCellLines(
    doc,
    summaryCol0InnerW,
    bundlesSummaryTxt,
    sumM3,
    sumPesoGross
  );
  const summaryMiddleContent = resolvePackingListSummaryText({
    summaryAdditionalText,
    productDescription,
    paisOrigen,
    hsCode,
    lugarEntrega,
    nombrePuertoDestino,
    paisDestino,
    detalleExcel,
  });

  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    pageBreak: 'avoid',
    rowPageBreak: 'avoid',
    tableId: SUMMARY_TABLE_ID,
    body: [
      [
        {
          content: totalsCellContent,
          styles: { valign: 'top', halign: 'left', fontStyle: 'normal', rowPageBreak: 'avoid' },
        },
        {
          content: summaryMiddleContent,
          styles: { valign: 'top', halign: 'left', fontStyle: 'normal', rowPageBreak: 'avoid' },
        },
        {
          content: '',
          styles: { valign: 'middle', halign: 'center', minCellHeight: 28, rowPageBreak: 'avoid' },
        },
      ],
    ],
    columnStyles: {
      0: {
        cellWidth: contentW * 0.34,
        fontSize: 8,
        valign: 'top',
        halign: 'left',
        lineWidth: 0,
        cellPadding: { top: SUMMARY_LEFT_EXTRA_TOP_PAD, left: 1, right: 1, bottom: 1 },
      },
      1: {
        cellWidth: contentW * 0.38,
        fontSize: 8,
        valign: 'top',
        halign: 'left',
        lineWidth: 0,
        cellPadding: { top: 0, left: 1, right: 1, bottom: 1 },
      },
      2: {
        cellWidth: contentW * 0.28,
        fontSize: 8,
        valign: 'top',
        halign: 'center',
        lineWidth: 0,
        cellPadding: { top: 0, left: 1, right: 1, bottom: 1 },
      },
    },
    styles: { lineWidth: 0, halign: 'left' },
    bodyStyles: {
      fontSize: 8,
      valign: 'top',
      halign: 'left',
      lineWidth: 0,
    },
    tableLineWidth: 0,
    theme: 'plain',
    didDrawCell: (data) => {
      const { doc: d, cell, table, section, column, row } = data;
      if (table.id !== SUMMARY_TABLE_ID) return;
      if (section !== 'body' || row.index !== 0 || column.index !== 2) return;
      const props = d.getImageProperties(summaryStampImage);
      const pad = 1;
      const maxW = Math.max(4, cell.width - 2 * pad);
      const maxH = Math.max(4, cell.height - 2 * pad);
      let w = maxW;
      let h = (props.height * w) / props.width;
      if (h > maxH) {
        h = maxH;
        w = (props.width * h) / props.height;
      }
      const x = cell.x + (cell.width - w) / 2;
      const yTop = cell.y + pad;
      d.addImage(summaryStampImage, 'PNG', x, yTop, w, h);
    },
  });

  removeTrailingBlankPages(doc);
  return doc;
}

export function packingListPdfFileName(params) {
  const inv = invoiceNumberCorrelativo(params?.numeroInvoice) || '';
  return `PackingList_${safe(inv).replace(/\W/g, '_') || 'export'}.pdf`;
}

/** Blob URL para vista previa en iframe (revocar con URL.revokeObjectURL al cerrar). */
export function createPackingListPdfBlobUrl(params) {
  return URL.createObjectURL(buildPackingListPdfDoc(params).output('blob'));
}

/** Genera y descarga PDF Packing List. */
export function generatePackingListPdf(params) {
  buildPackingListPdfDoc(params).save(packingListPdfFileName(params));
}

/**
 * Respuesta GET /packinglists/:id → parámetros de {@link generatePackingListPdf}.
 */
export function packingListFromApiToPdfParams(api) {
  if (!api) return {};
  return {
    numeroInvoice: api.numero_invoice || '',
    fechaEmbarque: api.fecha_embarque || '',
    clienteNombre: api.cliente_nombre_snapshot || '',
    clienteDireccion: api.cliente_direccion_snapshot || '',
    clienteCodigoPostal: api.cliente_codigo_postal || '',
    poLabel: api.po_label_snapshot || '',
    paisOrigen: api.pais_origen || '',
    paisDestino: api.pais_destino || '',
    nombreNave: api.nombre_nave || '',
    nombrePuertoOrigen: api.nombre_puerto_origen || '',
    nombrePuertoDestino: api.nombre_puerto_destino || '',
    lugarEntrega: api.lugar_entrega || '',
    nombreBl: api.nombre_bl || '',
    totalPaquetes: api.total_paquetes,
    totalContenedores: api.total_contenedores,
    detalleExcel: api.lineas || [],
    totalVolumenM3: api.total_volumen_m3,
    productDescription: api.product_description_snapshot || '',
    hsCode: api.hs_code_snapshot || '',
    summaryAdditionalText: api.texto_adicional_packing_list_snapshot || '',
  };
}

/**
 * Filas del detalle consolidadas para Excel (13 columnas).
 * Gross / Net / Container: una vez por grupo (como rowSpan del PDF); mergeSpans para Excel.
 */
export function packingListDetailRowsForExcel(detalleExcel, totalVolumenM3) {
  const detallePdf = consolidatePackingDetailForPdf(detalleExcel || []);
  const out = [];
  const mergeSpans = [];
  let i = 0;
  let lastContainerKey = null;

  while (i < detallePdf.length) {
    const groupContainerKey = detailContainerKey(detallePdf[i]);
    if (
      lastContainerKey != null &&
      groupContainerKey &&
      groupContainerKey !== lastContainerKey
    ) {
      out.push(Array(13).fill(''));
    }
    if (groupContainerKey) lastContainerKey = groupContainerKey;

    let j = i + 1;
    while (j < detallePdf.length && sameMergeGroup(detallePdf[j - 1], detallePdf[j])) {
      j += 1;
    }
    const span = j - i;
    const merge = span > 1;
    const startOutIndex = out.length;

    for (let k = i; k < j; k += 1) {
      const r = detallePdf[k];
      const row = [
        safe(r.nf_celda),
        productsCell(r),
        r.cantidad_paquetes != null ? String(r.cantidad_paquetes) : '',
        cellEspesor(r),
        widthCell(r),
        measureWidthLengthCell(r.largo),
        piecesPerBundleCell(r),
        isRwNrPackingListRow(r) ? '' : (r.total_piezas != null ? fmtIntegerCL(r.total_piezas) : ''),
        cbmPerBundleStr(r),
        r.total_m3 != null ? cellNum3CL(r.total_m3) : '',
      ];
      if (merge && k === i) {
        row.push(grossStr(r), netStr(r), containerPdf(r));
      } else if (merge && k > i) {
        row.push('', '', '');
      } else {
        row.push(grossStr(r), netStr(r), containerPdf(r));
      }
      out.push(row);
    }

    if (merge) {
      mergeSpans.push({ startOutIndex, endOutIndex: out.length - 1 });
    }
    i = j;
  }
  return {
    rows: out,
    mergeSpans,
    sumM3: computeSumM3(detalleExcel, totalVolumenM3),
    sumPesoGross: sumPesoGrossByMergeGroups(detallePdf),
    sumPesoNet: sumPesoNetByMergeGroups(detallePdf),
  };
}

/**
 * Filas del detalle: 13 columnas; Gross / Net / Container con rowSpan si el grupo consecutivo coincide.
 */
function buildDetailBody(detalleExcel) {
  const out = [];
  let i = 0;
  let lastContainerKey = null;

  while (i < detalleExcel.length) {
    const groupContainerKey = detailContainerKey(detalleExcel[i]);
    if (
      lastContainerKey != null &&
      groupContainerKey &&
      groupContainerKey !== lastContainerKey
    ) {
      out.push(detailContainerSeparatorRow());
    }
    if (groupContainerKey) lastContainerKey = groupContainerKey;

    let j = i + 1;
    while (j < detalleExcel.length && sameMergeGroup(detalleExcel[j - 1], detalleExcel[j])) {
      j += 1;
    }
    const span = j - i;
    const merge = span > 1;

    for (let k = 0; k < span; k += 1) {
      const r = detalleExcel[i + k];
      const m3 = r.total_m3 != null ? cellNum3CL(r.total_m3) : '';
      const m3PerBundle = cbmPerBundleStr(r);
      const base = [
        safe(r.nf_celda),
        productsCell(r),
        r.cantidad_paquetes != null ? String(r.cantidad_paquetes) : '',
        cellEspesor(r),
        widthCell(r),
        measureWidthLengthCell(r.largo),
        piecesPerBundleCell(r),
        isRwNrPackingListRow(r) ? '' : (r.total_piezas != null ? fmtIntegerCL(r.total_piezas) : ''),
        m3PerBundle,
        m3,
      ];

      if (merge && k === 0) {
        base.push(
          { content: grossStr(r), rowSpan: span, styles: { valign: 'middle' } },
          { content: netStr(r), rowSpan: span, styles: { valign: 'middle' } },
          { content: containerPdf(r), rowSpan: span, styles: { valign: 'middle' } }
        );
        out.push(base);
      } else if (merge && k > 0) {
        out.push(base);
      } else {
        base.push(grossStr(r), netStr(r), containerPdf(r));
        out.push(base);
      }
    }
    i = j;
  }
  return out;
}
